/**
 * Что кассовый агент умеет, по его `GET /v1/hello` (RP-OWN7): хватает ли ему фискальных маршрутов для собственного окна продажи
 * и не включён ли у него режим «1С». Чистые функции, без сети и без React.
 *
 * Договорённость с агентом: фискальные маршруты (park, repunch, correction, resolve-card, confirm-void, continue-print, итоги смены)
 * гарантирует `api.level >= 4`. Агент уровня 3 (сборка edfe4221) признаков фискальных маршрутов не отдаёт вовсе, поэтому
 * порогом служит уровень, а `api.features` — дополнение: агент, объявивший хотя бы один признак `fiscal-*`, обязан объявить все.
 * Старый агент не падает на 404 посреди продажи: окно закрывает продажу заранее понятным текстом, деньги не берутся.
 */

/** Минимальный `api.level` агента с полным набором фискальных маршрутов. */
export const AGENT_FISCAL_MIN_API_LEVEL = 4

/** Признаки в `api.features`, которые агент уровня 4 и выше отдаёт вместе с маршрутами. */
export const AGENT_FISCAL_FEATURES: readonly string[] = [
  'fiscal-park',
  'fiscal-repunch',
  'fiscal-correction',
  'fiscal-resolve-card',
  'fiscal-confirm-void',
  'fiscal-continue-print',
  'fiscal-shift-totals',
]

/** Режим рабочего места агента, при котором чеки ведёт 1С (`hello.workplaceMode`). */
export const WORKPLACE_MODE_ONEC = '1c'

/** Часть `hello`, нужная решению: уровень и признаки (`null` — агент без `api`) и режим рабочего места (`null` — не сказал). */
export interface AgentCapabilityInput {
  api: { level: number; features: readonly string[] } | null
  workplaceMode: string | null
}

export type FiscalSupport =
  | { kind: 'ok' }
  /** Агент не умеет часть фискальных маршрутов: обновить. */
  | { kind: 'outdated'; text: string }
  /** Агент в режиме «1С»: собственное окно продажи закрыто. */
  | { kind: 'onec-mode'; text: string }

export const OUTDATED_AGENT_PREFIX = 'Кассовый агент устарел и не умеет часть кассовых действий (отложить, повторить пробивку, чек коррекции, закрыть оплату картой, допечатать, итоги смены): обновите кассовый агент.'

export const ONEC_MODE_TEXT =
  'Агент работает в режиме 1С: чеки и смены ведёт 1С, поэтому собственное окно продажи закрыто. Продавайте из 1С или переключите рабочее место агента в собственный режим.'

export function fiscalSupportOf(agent: AgentCapabilityInput): FiscalSupport {
  if (agent.workplaceMode?.trim().toLowerCase() === WORKPLACE_MODE_ONEC) return { kind: 'onec-mode', text: ONEC_MODE_TEXT }
  const level = agent.api?.level ?? 0
  const outdated = (detail: string): FiscalSupport => ({ kind: 'outdated', text: `${OUTDATED_AGENT_PREFIX} Продажа закрыта, деньги не берутся. ${detail}` })
  if (level < AGENT_FISCAL_MIN_API_LEVEL) return outdated(`Уровень агента: ${level}, нужен: ${AGENT_FISCAL_MIN_API_LEVEL}.`)
  const features = agent.api?.features ?? []
  if (features.some((feature) => feature.startsWith('fiscal-'))) {
    const missing = AGENT_FISCAL_FEATURES.filter((feature) => !features.includes(feature))
    if (missing.length > 0) return outdated(`Агент не объявил возможности: ${missing.join(', ')}.`)
  }
  return { kind: 'ok' }
}
